import { FormField } from 'grommet';
import React, { useId } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { HiDownload } from 'react-icons/hi';

import { ClassificationFilter } from '@/components/classification-filter';
import { ClinicalsProductFilter } from '@/components/clinicals-product-filter';
import { RhfFilterForm } from '@/components/filters';
import { DateTextInput, InMemoryMultiSelectList } from '@/components/form-controls';
import { StateAndPoolFilter } from '@/components/state-and-pool-filter';
import { Button } from '@/components-new/button';

import { BoardMeetingFilterData, generateDefaultBoardMeetingFilters } from '../api';
import { useBoardMeetingExport } from '../hooks/use-board-meeting-export';
import { useBoardMeetingsProvider } from './board-meetings-provider';

type BoardMeetingFiltersProps = {
  /**
   * Initial value for the form, required.
   */
  defaultValues: BoardMeetingFilterData;

  /**
   * Submit handler to update.
   */
  onSubmit: (filterData: BoardMeetingFilterData) => void;
};

/**
 * Filters for the Board Meeting list.
 */
export const BoardMeetingFilters = ({ defaultValues, onSubmit }: BoardMeetingFiltersProps) => {
  const {
    control,
    handleSubmit,
    reset,
    watch
  } = useForm<BoardMeetingFilterData>({
    defaultValues
  });
  const { states, pools, meetingTypes, classifications } = useBoardMeetingsProvider();
  const id = useId();
  
  // Board meeting export functionality
  const { exportBoardMeetings, isExporting } = useBoardMeetingExport();
  
  const handleExportClick = async () => {
    // Get current filter values and pass them to export
    const currentFilters = watch();
    await exportBoardMeetings(currentFilters);
  };

  return (
    <RhfFilterForm
      watch={watch}
      onReset={() => reset(generateDefaultBoardMeetingFilters())}
      onSubmit={handleSubmit(onSubmit)}
      actions={
        <Button color="secondary" onClick={handleExportClick} disabled={isExporting}>
          <HiDownload/>
          {isExporting ? 'Exporting...' : 'Export CSV'}
        </Button>
      }
    >
      <Controller
        control={control}
        name="statesOrPools"
        render={({ field: { value, onChange } }) => (
          <StateAndPoolFilter
            states={states}
            pools={pools}
            selected={value}
            onSelectionChange={onChange}
          />
        )}
      />
      <Controller
        control={control}
        name="meetingTypes"
        render={({ field: { value, onChange } }) => (
          <InMemoryMultiSelectList
            placeholder="Search by Meeting Type"
            options={meetingTypes}
            value={value}
            onChange={onChange}
          />
        )}
      />
      <Controller
        control={control}
        name="classes"
        render={({ field: { value, onChange } }) => (
          <ClassificationFilter 
            label=""
            selected={value} 
            onSelectionChange={onChange}
            classifications={classifications}/>
        )}
      />
      <Controller
        control={control}
        name="drugs"
        render={({ field: { value, onChange } }) => (
          <ClinicalsProductFilter
            selected={value}
            onSelectionChange={onChange}
            dataSource="drugs"
            getTrackedOnly={true}
          />
        )}
      />
      <Controller
        control={control}
        name="dateRangeBegin"
        render={({ field: { name, onChange, onBlur, value } }) => (
          <FormField
            required
            name={name}
            htmlFor={`${id}-${name}`}
            label="Start"
            margin="none"
          >
            <DateTextInput
              name={name}
              id={`${id}-${name}`}
              value={value}
              onChange={({ target }) => onChange((target as any).value)}
              onBlur={onBlur}
            />
          </FormField>
        )}
      />
      <Controller
        control={control}
        name="dateRangeEnd"
        render={({ field: { name, onChange, onBlur, value } }) => (
          <FormField
            required
            name={name}
            label="End"
            htmlFor={`${id}-${name}`}
            margin="none"
          >
            <DateTextInput
              name={name}
              id={`${id}-${name}`}
              value={value}
              onChange={({ target }) => onChange((target as any).value)}
              onBlur={onBlur}
            />
          </FormField>
        )}
      />
    </RhfFilterForm>
  );
};
